import type { Recipient } from "@mpa/toolbox/models/Email";

import { PromiseToast } from "../../components/async/PromiseToast";
import Button from "../../components/Button/Button";
import EmptyView from "../../components/EmptyView";
import type { BriefFieldProps } from "../../components/form/BriefField";
import BriefField from "../../components/form/BriefField";
import Form from "../../components/form/Form";
import SearchableSelectField, {
    type SearchableSelectFieldProps,
} from "../../components/form/SearchableSelectField";
import TextField, { type TextFieldProps } from "../../components/form/TextField";
import useField from "../../components/form/useField";
import LightPageLayout from "../../components/Page/LightPageLayout";
import PageHeader from "../../components/Page/PageHeader";
import Spacer from "../../components/space/Spacer";
import { callOutgoingRequest, useOutgoingRequest } from "../../data/tanstack-query";
import { searchEmailRecipients, sendEmail } from "../../endpoints/outgoing-requests";
import useSubmit from "../../hooks/useSubmit";
import { navigate } from "../../navigation/location";
import { mailingRoute } from "./navigation";

export default function NewMailPage() {
    const recipients = useOutgoingRequest(searchEmailRecipients(""));
    const receipientField = useField<SearchableSelectFieldProps<Recipient>>({
        label: "Destinataire",
        placeholder: "Rechercher par prénom, nom ou email",
        value: undefined,
        keyExtractor: (recipient) => recipient.partyAttributeId,
        labelExtractor: (recipient) => recipient.label,
        searchItems: searchEmailRecipients,
        required: true,
    });
    const subjectField = useField<TextFieldProps>({
        label: "Sujet",
        value: undefined,
        required: true,
    });
    const briefField = useField<BriefFieldProps>({
        label: "Contenu",
        value: undefined,
        required: true,
    });

    const [onSend, sending] = useSubmit(async () => {
        const recipient = receipientField.validate();
        const subject = subjectField.validate().trim();
        const brief = briefField.validate();
        await callOutgoingRequest(
            sendEmail({
                partyAttributeId: recipient.partyAttributeId,
                subject: subject,
                blocks: brief.blocks,
            }),
        );
        navigate(mailingRoute.getLocation());
    }, []);

    return (
        <LightPageLayout>
            <PromiseToast promise={sending} message="Email envoyé" />
            <PageHeader
                title="Nouveau mail"
                action={<Button label="Envoyer" action={onSend} feeling="minor" role="minor" />}
            />
            {recipients.length === 0 ? (
                <EmptyView message="Aucune partie avec une adresse email" />
            ) : (
                <Form onSubmit={onSend}>
                    <SearchableSelectField {...receipientField.props} />
                    <Spacer scale={4} />
                    <TextField {...subjectField.props} />
                    <Spacer scale={4} />
                    <BriefField {...briefField.props} />
                </Form>
            )}
        </LightPageLayout>
    );
}
